import React, { useState, useRef } from 'react';
import { ChevronDown, Building2, Check, MapPin, Loader2, Shield, LogOut, Key } from 'lucide-react';
import { useWorkspace, permissionCategories } from '../contexts/WorkspaceContext';
import { ConfirmModal } from './ConfirmModal';
export function Header() {
  const {
    currentWorkspace,
    currentUser,
    logout,
    loading,
    workspaces,
    setCurrentWorkspace,
    setCurrentSubRegion,
    currentSubRegion,
    getUserPermissions
  } = useWorkspace();
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const [isTeamOpen, setIsTeamOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const primaryColor = currentWorkspace.primaryColor || '#2563eb';
  if (loading) {
    return <header className="h-16 border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8 flex items-center flex-shrink-0" data-id="element-1906" data-mp="3fbgl5hblp6vs">
        <Loader2 className="h-5 w-5 animate-spin mr-3" style={{
        color: primaryColor
      }} data-id="element-1907" data-mp="2wl4dylzmjye3" />
        <span className="text-sm text-gray-500" data-id="element-1908" data-mp="2rkghhksmmpch">Loading workspace...</span>
      </header>;
  }
  const roleBadge = currentUser.Role === 'Super Admin' ? 'bg-purple-50 text-purple-700 ring-purple-700/10' : currentUser.Role === 'Team Manager' ? 'bg-blue-50 text-blue-700 ring-blue-700/10' : 'bg-gray-50 text-gray-700 ring-gray-700/10';
  // Get user's permissions grouped by category
  const userPermissions = getUserPermissions();
  const permissionsByCategory = Object.entries(permissionCategories).map(([category, perms]) => {
    const granted = perms.filter(p => userPermissions.includes(p as any));
    return {
      category,
      total: perms.length,
      granted: granted.length,
      permissions: granted
    };
  }).filter(c => c.granted > 0);
  return <header className="h-16 border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-shrink-0" data-id="element-1909" data-mp="1rdkzu5zy6xu8">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1" data-id="element-1910" data-mp="1wbmk2u94137a">
        <span className="text-sm text-gray-500 hidden sm:inline flex-shrink-0" data-id="element-1911" data-mp="1ho1gkz8bp1yu">
          Scope:
        </span>

        {/* Team Selector */}
        <div className="relative flex-shrink-0" data-id="element-1912" data-mp="1mljrgv5whr9h">
          <button onClick={() => setIsTeamOpen(!isTeamOpen)} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors whitespace-nowrap" data-id="element-1913" data-mp="18nbpu9xx61rz">
            <Building2 className="h-4 w-4 text-gray-500 flex-shrink-0" data-id="element-1914" data-mp="13nz63y2chatm" />
            <span className="truncate max-w-[100px] sm:max-w-[150px]" data-id="element-1915" data-mp="13q35c2patlnk">
              {currentWorkspace.name}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isTeamOpen ? 'rotate-180' : ''}`} data-id="element-1916" data-mp="2y1ro6fyni8d8" />
          </button>

          {isTeamOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsTeamOpen(false)} data-id="element-1917" data-mp="hgcx8xvjlb5b" />
              <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20 max-h-80 overflow-y-auto" data-id="element-1918" data-mp="w5sk42rycwfq">
                {workspaces.length === 0 ? <div className="px-4 py-3 text-sm text-gray-500 italic" data-id="element-1919" data-mp="153balmod82z0">
                    No teams available. Go to{' '}
                    <a href="/regions" className="text-blue-600 underline" data-id="element-1920" data-mp="1f3bvhd1vur5b">
                      Regions
                    </a>{' '}
                    to set up your first team.
                  </div> : workspaces.map(team => <button key={team.id} onClick={() => {
              setCurrentWorkspace(team);
              setCurrentSubRegion(null);
              setIsTeamOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-1921" data-mp="3guyqrqst587l">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-1922" data-mp="2gyg8z8htuvi1">
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-1923" data-mp="2gxn98rhad69e" />
                        <div className="text-left min-w-0" data-id="element-1924" data-mp="1k9qu7r1hyj0a">
                          <div className="font-medium text-gray-900 truncate" data-id="element-1925" data-mp="1p6qzw0ufpjqv">
                            {team.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate" data-id="element-1926" data-mp="1t7kjwj4raeeh">
                            {team.subRegions.length} region
                            {team.subRegions.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      {currentWorkspace.id === team.id && <Check className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-1927" data-mp="y00k6go6jyfe" />}
                    </button>)}
              </div>
            </>}
        </div>

        <span className="text-gray-300 flex-shrink-0" data-id="element-1928" data-mp="f0mjzgzuxrgg">/</span>

        {/* Subregion Selector */}
        <div className="relative flex-shrink-0" data-id="element-1929" data-mp="aa647q8kdtqx">
          <button onClick={() => setIsRegionOpen(!isRegionOpen)} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors whitespace-nowrap" data-id="element-1930" data-mp="a8mpw56gxsrs">
            <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" data-id="element-1931" data-mp="3sf9fjsp4j1zs" />
            <span className="truncate max-w-[80px] sm:max-w-[120px]" data-id="element-1932" data-mp="3ngg03uqa10wz">
              {currentSubRegion || 'All Regions'}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isRegionOpen ? 'rotate-180' : ''}`} data-id="element-1933" data-mp="3drskuh56st3n" />
          </button>

          {isRegionOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsRegionOpen(false)} data-id="element-1934" data-mp="38u0uvso3rh23" />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20 max-h-80 overflow-y-auto" data-id="element-1935" data-mp="dxpjtkgq4gx">
                <button onClick={() => {
              setCurrentSubRegion(null);
              setIsRegionOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-1936" data-mp="5f4tfhjhijib">
                  <span className="font-medium text-gray-900" data-id="element-1937" data-mp="o3i6k6x22rnk">All Regions</span>
                  {!currentSubRegion && <Check className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-1938" data-mp="28tf6wrlfjp6r" />}
                </button>
                {currentWorkspace.subRegions.length > 0 && <div className="border-t border-gray-100 my-1" data-id="element-1939" data-mp="2dlyodzn8n4gu" />}
                {currentWorkspace.subRegions.map(region => <button key={region} onClick={() => {
              setCurrentSubRegion(region);
              setIsRegionOpen(false);
            }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors" data-id="element-1940" data-mp="1px5us9u398pe">
                    <span className="font-medium text-gray-900 truncate" data-id="element-1941" data-mp="3a9lcnaa5rgj7">
                      {region}
                    </span>
                    {currentSubRegion === region && <Check className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-1942" data-mp="13o8lnjt1ujeq" />}
                  </button>)}
                {currentWorkspace.subRegions.length === 0 && <div className="px-4 py-2 text-xs text-gray-500 italic" data-id="element-1943" data-mp="1mdf8nj0vlx6c">
                    No regions defined for this team
                  </div>}
              </div>
            </>}
        </div>
      </div>

      {/* User info + permissions dropdown */}
      <div className="relative flex items-center gap-3 flex-shrink-0 ml-4" data-id="element-1944" data-mp="1w9i6wdslxbo0">
        <button onClick={() => setIsUserOpen(!isUserOpen)} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors" data-id="element-1945" data-mp="21a5zv50ec2kv">
          <span className="hidden sm:inline text-sm text-gray-500 truncate max-w-[150px]" data-id="element-1946" data-mp="6xokejlsu6qs">
            {currentUser.User_Name}
          </span>
          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${roleBadge}`} data-id="element-1947" data-mp="2k8rceyfoxpc4">
            {currentUser.Role}
          </span>
          <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isUserOpen ? 'rotate-180' : ''}`} data-id="element-1948" data-mp="26frvqt469p2z" />
        </button>

        {isUserOpen && <>
            <div className="fixed inset-0 z-10" onClick={() => setIsUserOpen(false)} data-id="element-1949" data-mp="2z4goo4v2qo3n" />
            <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden" data-id="element-1950" data-mp="2u4b1p1zr1prw">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100" style={{
            backgroundColor: 'var(--primary-50, #eff6ff)'
          }} data-id="element-1951" data-mp="1ipnqkhbc5h9v">
                <div className="flex items-center gap-3" data-id="element-1952" data-mp="1d2e5ejvg2o7d">
                  <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{
                backgroundColor: primaryColor
              }} data-id="element-1953" data-mp="1d0tagt57x38z">
                    {currentUser.User_Name?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0" data-id="element-1954" data-mp="1vs3zf4rdv5y8">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1955" data-mp="1qucbsmxru5zu">
                      {currentUser.User_Name}
                    </p>
                    <p className="text-xs text-gray-500 truncate" data-id="element-1956" data-mp="25qa39rwr2m5q">
                      {currentUser.Email}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2" data-id="element-1957" data-mp="bcsbfspbb34w">
                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${roleBadge}`} data-id="element-1958" data-mp="bfetim9tx11d">
                    {currentUser.Role}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-1959" data-mp="2n16yn6j1n9ov">
                    Team: {currentUser.Team}
                  </span>
                </div>
              </div>

              {/* Permissions summary */}
              <div className="px-4 py-3 max-h-60 overflow-y-auto" data-id="element-1960" data-mp="2d9t3tc21ey22">
                <div className="flex items-center gap-1.5 mb-2" data-id="element-1961" data-mp="1zbl26qu238kk">
                  <Key className="h-3.5 w-3.5 text-gray-400" data-id="element-1962" data-mp="1zdfmq41f0yo4" />
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1963" data-mp="23dpqrb5ndf0c">
                    Permissions
                  </span>
                </div>
                {currentUser.Role === 'Super Admin' ? <div className="flex items-center gap-2 py-1.5" data-id="element-1964" data-mp="1uecfqe1vkidi">
                    <Shield className="h-4 w-4 flex-shrink-0" style={{
                color: primaryColor
              }} data-id="element-1965" data-mp="3jshlhuq9myct" />
                    <span className="text-sm text-gray-700" data-id="element-1966" data-mp="3naoswzdh06w7">
                      Full access — all permissions granted
                    </span>
                  </div> : permissionsByCategory.length === 0 ? <p className="text-sm text-gray-500 py-1" data-id="element-1967" data-mp="3dcr68gb9ei5p">
                    No permissions assigned
                  </p> : <div className="space-y-1.5" data-id="element-1968" data-mp="9t5y8w0me1mu">
                    {permissionsByCategory.map(({
                category,
                total,
                granted
              }) => <div key={category} className="flex items-center justify-between py-1" data-id="element-1969" data-mp="4ebd2af27is">
                          <span className="text-sm text-gray-700" data-id="element-1970" data-mp="3rafgyvbm48x1">
                            {category}
                          </span>
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{
                  backgroundColor: granted === total ? 'var(--primary-50, #eff6ff)' : '#f9fafb',
                  color: granted === total ? 'var(--primary-700, #1d4ed8)' : '#6b7280'
                }} data-id="element-1971" data-mp="nu8mnff37o0x">
                            {granted}/{total}
                          </span>
                        </div>)}
                  </div>}
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 px-4 py-2" data-id="element-1972" data-mp="1ss3ulgmdqzcp">
                <button onClick={() => {
              setIsUserOpen(false);
              setShowSignOutConfirm(true);
            }} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-1973" data-mp="1xt1atnntxjbk">
                  <LogOut className="h-4 w-4" data-id="element-1974" data-mp="2uhixua1w45pp" />
                  Sign out
                </button>
              </div>
            </div>
          </>}
      </div>

      <ConfirmModal isOpen={showSignOutConfirm} title="Sign Out" message="Sign out of ConfigConsole?" confirmLabel="Sign Out" variant="danger" onCancel={() => setShowSignOutConfirm(false)} onConfirm={() => {
      setShowSignOutConfirm(false);
      logout();
    }} data-id="element-1975" data-mp="34ahp5zoruad0" />
    </header>;
}